import React, { useState, useEffect } from "react";
import {
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiModalFooter,
  EuiButton,
  EuiButtonEmpty,
  EuiFieldText,
  EuiFormRow,
  EuiSpacer,
  EuiText,
  EuiFlexGroup,
  EuiFlexItem,
  EuiButtonIcon,
  EuiPanel,
  EuiEmptyPrompt,
  EuiLoadingSpinner,
  EuiCallOut,
} from "@elastic/eui";
import { getApiUrl } from "../utils/api";
import { PostOptions } from "../utils/RequestUtils";

const TAManagementDialog = ({ isModalVisible, setIsModalVisible, course }) => {
  const [newTAEmail, setNewTAEmail] = useState("");
  const [currentTAs, setCurrentTAs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const getCourseId = () => {
    return course?._id || course?.id;
  };

  useEffect(() => {
    if (isModalVisible && course) {
      fetchCurrentTAs();
    }
  }, [isModalVisible, course]);

  const fetchCurrentTAs = async () => {
    setIsLoading(true);
    setError("");

    try {
      const courseId = getCourseId();
      const response = await fetch(getApiUrl(`/api/course/${courseId}/tas`), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to fetch TAs");
      }

      setCurrentTAs(data.tas || []);
    } catch (err) {
      setError("Failed to load current TAs: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTA = async () => {
    if (!newTAEmail.trim()) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setError("");
    setSuccess("");

    try {
      const courseId = getCourseId();
      const response = await fetch(
        getApiUrl("/api/course/add-ta"),
        PostOptions({
          courseId: courseId,
          username: newTAEmail.trim(),
        })
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to add TA");
      }

      setSuccess(`Successfully added ${newTAEmail} as TA`);
      setNewTAEmail("");

      // Refresh the TA list
      await fetchCurrentTAs();
    } catch (err) {
      setError("Failed to add TA: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveTA = async (taId, taEmail) => {
    if (!window.confirm(`Are you sure you want to remove ${taEmail} as TA?`)) {
      return;
    }

    try {
      const courseId = getCourseId();
      const response = await fetch(
        getApiUrl("/api/course/remove-ta"),
        PostOptions({
          courseId: courseId,
          taId: taId,
        })
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || "Failed to remove TA");
      }

      setSuccess(`Successfully removed ${taEmail} as TA`);

      // Refresh the TA list
      await fetchCurrentTAs();
    } catch (err) {
      setError("Failed to remove TA: " + err.message);
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
    setNewTAEmail("");
    setCurrentTAs([]);
    setError("");
    setSuccess("");
  };

  if (!isModalVisible) return null;

  return (
    <EuiModal onClose={closeModal} style={{ width: 600 }}>
      <EuiModalHeader>
        <EuiModalHeaderTitle>
          TA Management
        </EuiModalHeaderTitle>
      </EuiModalHeader>

      <EuiModalBody>
        {/* Success/Error Messages */}
        {error && (
          <>
            <EuiCallOut title="Error" color="danger" iconType="alert">
              {error}
            </EuiCallOut>
            <EuiSpacer size="m" />
          </>
        )}

        {success && (
          <>
            <EuiCallOut title="Success" color="success" iconType="check">
              {success}
            </EuiCallOut>
            <EuiSpacer size="m" />
          </>
        )}

        <EuiFlexGroup alignItems="flexEnd">
          <EuiFlexItem>
            <EuiFormRow label="Email Address">
              <EuiFieldText
                placeholder="Enter TA email address"
                value={newTAEmail}
                onChange={(e) => setNewTAEmail(e.target.value)}
                onKeyPress={(e) => {
                  if (e.key === "Enter") {
                    handleAddTA();
                  }
                }}
                disabled={isSubmitting}
              />
            </EuiFormRow>
          </EuiFlexItem>
          <EuiFlexItem grow={false}>
            <EuiButton
              fill
              onClick={handleAddTA}
              isLoading={isSubmitting}
              disabled={!newTAEmail.trim()}
            >
              Add TA
            </EuiButton>
          </EuiFlexItem>
        </EuiFlexGroup>

        <EuiSpacer size="l" />

        {/* Current TAs Section */}
        <EuiText>
          <h3>Current Teaching Assistants</h3>
        </EuiText>
        <EuiSpacer size="s" />

        {isLoading ? (
          <EuiFlexGroup justifyContent="center">
            <EuiFlexItem grow={false}>
              <EuiLoadingSpinner size="l" />
            </EuiFlexItem>
          </EuiFlexGroup>
        ) : currentTAs.length === 0 ? (
          <EuiEmptyPrompt
            iconType="users"
            title={<h4>No Teaching Assistants</h4>}
            body={
              <p>
                This course currently has no teaching assistants. Add TAs using
                the form above.
              </p>
            }
          />
        ) : (
          <div>
            {currentTAs.map((ta, index) => (
              <EuiPanel key={ta.id || index} paddingSize="s">
                <EuiFlexGroup alignItems="center" justifyContent="spaceBetween">
                  <EuiFlexItem>
                    <EuiText size="s">
                      <strong>{ta.name || ta.email}</strong>
                      {ta.name && (
                        <div style={{ color: "#69707D" }}>{ta.email}</div>
                      )}
                    </EuiText>
                  </EuiFlexItem>
                  <EuiFlexItem grow={false}>
                    <EuiButtonIcon
                      iconType="trash"
                      color="danger"
                      aria-label={`Remove ${ta.email} as TA`}
                      onClick={() => handleRemoveTA(ta.id, ta.email)}
                      title={`Remove ${ta.email}`}
                    />
                  </EuiFlexItem>
                </EuiFlexGroup>
              </EuiPanel>
            ))}
          </div>
        )}
      </EuiModalBody>

      <EuiModalFooter>
        <EuiButtonEmpty onClick={closeModal}>Close</EuiButtonEmpty>
      </EuiModalFooter>
    </EuiModal>
  );
};

export default TAManagementDialog;
